import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Avatar,
  Badge,
  Box,
  Button,
  ListItem,
  Popover,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
  useTheme
} from '@mui/material';
import PropTypes from 'prop-types';
import { FC, ReactNode, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OldNavLinkV5 } from 'src/components/NavLink';
import { SupportUserIcon } from 'src/components/SupportUserIcon';
import { SidebarContext } from 'src/contexts/SidebarContext';

const TooltipWrapper = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.colors.alpha.black[100],
    color: theme.palette.getContrastText(theme.colors.alpha.black[100]),
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 'bold',
    borderRadius: theme.general.borderRadiusSm,
    boxShadow:
      '0 .2rem .8rem rgba(7,9,25,.18), 0 .08rem .15rem rgba(7,9,25,.15)'
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.colors.alpha.black[100]
  }
}));

interface HeaderMenuItemProps {
  children?: ReactNode;
  link?: string;
  icon?: any;
  features?: string[];
  badge?: string;
  badgeTooltip?: string;
  open?: boolean;
  active?: boolean;
  name: string;
  description?: string;
  index?: number;
}

const IndicatorWrapper = styled(Box)(
  () => `
  width: 16px;
  height: 16px;
  margin-right: -8px;

  .MuiSvgIcon-root {
    width: 100%;
    height: auto;
  }
`
);

const PopoverWrapper = styled(Popover)(
  ({ theme }) => `
  margin-top: 0.8rem;
  max-width:90%;

  .MuiList-root {
    padding: ${theme.spacing(1)} !important;

    .MuiListItem-root {
      padding: 2px 0 !important;
      min-width: ${theme.spacing(20)};
      max-width: ${theme.spacing(40)};
      
      .nameWrapper {
        color: ${theme.palette.secondary.main} !important;
      }

      &:last-child {
          margin-left: 0;
        }
      }


      .MuiIconButton-root {
        width: 100% !important;
        height: auto !important;
        justify-content: flex-start !important;
        font-weight: bold !important;
        padding: ${theme.spacing(1, 2)} !important;

        .MuiBadge-root {
          right: 20px !important;
          top: 20px !important;

          .MuiBadge-badge {
            background: ${theme.colors.primary.main} !important;
            color: ${theme.palette.getContrastText(
              theme.colors.primary.main
            )} !important;
          }
        }

        .name-wrapper {
          transition: ${theme.transitions.create(['all'])};
          color: ${theme.palette.secondary.main};
        }

        &.Mui-active,
        &:hover {

          .name-wrapper {
            
          }
        }
      }
    }  
  }
`
);

const SubMenuItemTItle = styled(Typography)(
  ({ theme }) => `
  font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
        `
);
const SubMenuItemDescription = styled(Typography)(
  ({ theme }) => `
        color: ${theme.palette.secondary.light};
        `
);

const HeaderMenuItem: FC<HeaderMenuItemProps> = ({
  children,
  link,
  icon: Icon,
  features,
  badge,
  badgeTooltip,
  open: openParent,
  active,
  name,
  description,
  index,
  ...rest
}) => {
  const { t } = useTranslation();
  const { closeSidebar } = useContext(SidebarContext);
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  // @ts-ignore next-line
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  if (children) {
    return (
      <ListItem
        component="div"
        className="Mui-children"
        key={name}
        {...rest}
        sx={{ display: { xs: 'inline', md: name === 'Settings' && 'none' } }} // Hide settings on desktop
      >
        <Button
          disableRipple
          onClick={handleClick}
          sx={{
            color: theme.palette.secondary.light,
            textAlign: 'center',
            '&:hover': {
              color: theme.palette.secondary.main,
              background: theme.colors.alpha.black[10]
            }
          }}
        >
          <span className="name-wrapper">{t(name)}</span>
          <SupportUserIcon features={features} fontSize="small" />
          <IndicatorWrapper>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IndicatorWrapper>
        </Button>
        <PopoverWrapper
          classes={{ root: 'child-popover' }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          anchorEl={anchorEl}
          onClose={handleClose}
          onClick={handleClose}
          open={open}
        >
          {children}
        </PopoverWrapper>
      </ListItem>
    );
  }

  const popoverItemColors = [
    theme.colors.primary,
    theme.colors.success,
    theme.colors.info,
    theme.colors.warning,
    theme.colors.error
  ];

  const popoverItemColor =
    popoverItemColors[index % (popoverItemColors.length - 1)];

  // Add description for menu items that have them
  const buttonContents =
    description && Icon ? (
      <Box
        display="flex"
        alignItems="start"
        gap={2}
        sx={{
          margin: theme.spacing(0, -1)
        }}
      >
        <Avatar
          sx={{
            backgroundColor: popoverItemColor.lighter,
            color: popoverItemColor.main,
            width: theme.spacing(6),
            height: theme.spacing(6)
          }}
        >
          <Icon />
        </Avatar>
        <Box display={'flex'} flexDirection={'column'}>
          <SubMenuItemTItle variant={'body1'}>{t(name)}</SubMenuItemTItle>
          <SubMenuItemDescription variant={'body2'}>
            {t(description)}
          </SubMenuItemDescription>
        </Box>
        <SupportUserIcon features={features} />
      </Box>
    ) : (
      t(name)
    );

  return (
    <ListItem component="div" key={name} {...rest}>
      <Button
        disableRipple
        activeClassName="Mui-active"
        component={OldNavLinkV5}
        onClick={closeSidebar}
        to={link}
        fullWidth
        disabled={active}
        sx={{
          justifyContent: 'flex-start',
          gap: 2,
          color: theme.palette.secondary.light,
          '&:hover': {
            color: theme.palette.secondary.main,
            background: theme.colors.alpha.black[10]
          },
          '&.Mui-active': {
            color: theme.palette.secondary.main,
            background: theme.colors.alpha.black[10]
          }
        }}
      >
        {buttonContents}
        {badgeTooltip ? (
          <TooltipWrapper title={badgeTooltip} arrow placement="right">
            {badge && <Badge badgeContent={badge} />}
          </TooltipWrapper>
        ) : (
          badge && <Badge badgeContent={badge} />
        )}
      </Button>
    </ListItem>
  );
};

HeaderMenuItem.propTypes = {
  children: PropTypes.node,
  active: PropTypes.bool,
  link: PropTypes.string,
  icon: PropTypes.elementType,
  badge: PropTypes.string,
  badgeTooltip: PropTypes.string,
  open: PropTypes.bool,
  name: PropTypes.string.isRequired
};

HeaderMenuItem.defaultProps = {
  open: false,
  active: false
};

export default HeaderMenuItem;
